import React, { useState } from 'react';
import SearchableTicker from '../SearchableTicker';
import ExpirationDateSelect from '../Watchlist/ExpirationDateSelect';
import StrikePriceSelect from '../Watchlist/StrikePriceSelect';
import apiClient from '../../services/api';
import { actionButtonTemplate } from '../../theme';
import {
  isUsageLimitError,
  getUsageLimitData,
  isFeatureAccessError,
  getFeatureAccessData,
} from '../../utils/usageLimitHelper';
import UsageLimitModal from '../UsageLimitModal';
import FeatureAccessModal from '../FeatureAccessModal';
import { ASSET_TYPES } from './portfolioConstants';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';

const emptyRow = (assetType = ASSET_TYPES.OPTION) => ({
  asset_type: assetType,
  ticker: '',
  direction: assetType === ASSET_TYPES.STOCK ? 'buy_stock' : 'buy_call',
  option_type: assetType === ASSET_TYPES.STOCK ? null : 'call',
  position_type: 'buy',
  strike: '',
  expiration: '',
  days_to_gain: '',
  number_of_contracts: '',
  average_cost_per_contract: '',
});

/**
 * Add positions to the portfolio.
 *
 * Forked from WatchlistParamsForm rather than shared with it, because the
 * asset-type branch changes which fields exist, what they're called, and what
 * counts as a valid row — pushing that into the watchlist's form would put
 * portfolio-only branching in a component the watchlist depends on.
 *
 * Unlike the watchlist, saving here does NOT open a group-assignment modal:
 * there is exactly one destination, so rows go straight into the portfolio.
 */
export default function PortfolioParamsForm({ portfolioId, onSaved }) {
  const [rows, setRows] = useState([emptyRow()]);
  const [isSaving, setIsSaving] = useState(false);
  const [usageLimitData, setUsageLimitData] = useState(null);
  const [featureAccessData, setFeatureAccessData] = useState(null);
  const [error, setError] = useState('');

  const isStock = (row) => row.asset_type === ASSET_TYPES.STOCK;

  const handleRowChange = (index, updatedFields) => {
    setRows((prev) => {
      const updated = [...prev];
      const current = updated[index];

      // Switching asset type rebuilds the row — an option's strike/expiration
      // are meaningless on a stock, and vice versa.
      if (updatedFields.asset_type !== undefined && updatedFields.asset_type !== current.asset_type) {
        updated[index] = {
          ...emptyRow(updatedFields.asset_type),
          ticker: current.ticker,
          number_of_contracts: current.number_of_contracts,
          average_cost_per_contract: current.average_cost_per_contract,
        };
        return updated;
      }

      // Ticker change clears the contract identity that depends on it.
      if (updatedFields.ticker !== undefined && updatedFields.ticker !== current.ticker) {
        updated[index] = { ...current, ...updatedFields, expiration: '', strike: '' };
        return updated;
      }

      // Direction encodes both sides of the position: `buy_call` -> buy + call,
      // `buy_stock` -> buy + (no option type).
      if (updatedFields.direction !== undefined && updatedFields.direction !== current.direction) {
        const [posType, optType] = updatedFields.direction.split('_');
        updated[index] = {
          ...current,
          ...updatedFields,
          position_type: posType,
          option_type: optType === 'stock' ? null : optType,
          strike: optType === 'stock' ? '' : '',
        };
        return updated;
      }

      if (updatedFields.expiration !== undefined && updatedFields.expiration !== current.expiration) {
        updated[index] = { ...current, ...updatedFields, strike: '' };
        return updated;
      }

      updated[index] = { ...current, ...updatedFields };
      return updated;
    });
  };

  const handleAddRow = (assetType) => setRows((prev) => [...prev, emptyRow(assetType)]);

  const handleRemoveRow = (index) =>
    setRows((prev) => prev.filter((_, i) => i !== index));

  const handleDuplicateRow = (index) =>
    setRows((prev) => [...prev.slice(0, index + 1), { ...prev[index] }, ...prev.slice(index + 1)]);

  const validate = () => {
    for (const row of rows) {
      if (!row.ticker) return 'Every position needs a ticker.';
      if (!isStock(row) && (!row.strike || !row.expiration)) {
        return 'Option positions need both a strike and an expiration date.';
      }
    }
    return '';
  };

  const handleSave = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setError('');
    setIsSaving(true);

    try {
      const savedIds = [];

      for (const row of rows) {
        const payload = {
          asset_type: row.asset_type,
          ticker: row.ticker,
          position_type: row.position_type || 'buy',
        };

        if (!isStock(row)) {
          payload.option_type = row.option_type;
          payload.strike = parseFloat(row.strike);
          payload.expiration = row.expiration;
          if (row.days_to_gain) payload.days_to_gain = parseInt(row.days_to_gain, 10);
        }

        if (row.number_of_contracts) {
          payload.number_of_contracts = parseInt(row.number_of_contracts, 10);
        }
        if (row.average_cost_per_contract) {
          payload.average_cost_per_contract = parseFloat(row.average_cost_per_contract);
        }

        const response = await apiClient.post('/saved-contracts/', payload);
        savedIds.push(response.data.id);
      }

      // One destination — assign straight to the portfolio, no modal.
      if (savedIds.length > 0 && portfolioId) {
        await apiClient.post(`/watchlist-groups/${portfolioId}/assign-contracts/`, {
          contract_ids: savedIds,
          mode: 'append',
        });
      }

      setRows([emptyRow()]);
      if (onSaved) await onSaved();
    } catch (err) {
      console.error('❌ Error saving positions:', err);
      if (isUsageLimitError(err)) {
        setUsageLimitData(getUsageLimitData(err));
      } else if (isFeatureAccessError(err)) {
        setFeatureAccessData(getFeatureAccessData(err));
      } else {
        const detail =
          err.response?.data?.error ||
          err.response?.data?.detail ||
          JSON.stringify(err.response?.data || {}) ||
          err.message;
        setError(`Could not add position: ${detail}`);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const assetToggleClass = (selected) =>
    `pf-seg__btn ${selected ? 'pf-seg__btn--active' : ''}`;

  return (
    <div className="space-y-4 mb-6" data-tour="portfolio-form">
      {error && (
        <div className="bg-red-900/30 border border-red-500 text-red-300 px-4 py-2 rounded">
          {error}
        </div>
      )}

      {rows.map((row, index) => {
        const stock = isStock(row);

        return (
          <div key={index} className="pf-panel space-y-3">
            <div className="flex justify-between items-center mb-2 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <span className="font-bold text-brand-blue">Position {index + 1}</span>
                <div className="pf-seg ml-2">
                  <button
                    type="button"
                    onClick={() => handleRowChange(index, { asset_type: ASSET_TYPES.OPTION })}
                    className={assetToggleClass(!stock)}
                    data-tooltip-id="portfolio-tooltip"
                    data-tooltip-content="An option contract — needs a strike and expiration."
                  >
                    Option
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRowChange(index, { asset_type: ASSET_TYPES.STOCK })}
                    className={assetToggleClass(stock)}
                    data-tooltip-id="portfolio-tooltip"
                    data-tooltip-content="Plain shares of the underlying. Tracked for cost basis and P&L; not included in the simulation views."
                  >
                    Stock
                  </button>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => handleDuplicateRow(index)} className="btn-ghost-primary">
                  Duplicate
                </button>
                <button
                  onClick={() => handleRemoveRow(index)}
                  className="btn-ghost-danger"
                  disabled={rows.length <= 1}
                  style={rows.length <= 1 ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
                >
                  Remove
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="filter-heading">TICKER</label>
                <SearchableTicker
                  value={row.ticker ? { label: row.ticker, value: row.ticker } : null}
                  onChange={(selected) => handleRowChange(index, { ticker: selected?.value || '' })}
                />
              </div>

              <div>
                <label
                  className="filter-heading cursor-help"
                  data-tooltip-id="portfolio-tooltip"
                  data-tooltip-content={
                    stock
                      ? 'Long = you own the shares. Short = you are short the shares.'
                      : 'Buy Calls = bullish long. Buy Puts = bearish long. Sell Puts = bullish short. Sell Calls = bearish short.'
                  }
                >
                  DIRECTION
                </label>
                <select
                  className="input"
                  value={row.direction}
                  onChange={(e) => handleRowChange(index, { direction: e.target.value })}
                >
                  {stock ? (
                    <>
                      <option value="buy_stock">Long (Own Shares)</option>
                      <option value="sell_stock">Short</option>
                    </>
                  ) : (
                    <>
                      <option value="buy_call">Buy Calls</option>
                      <option value="buy_put">Buy Puts</option>
                      <option value="sell_put">Sell Puts</option>
                      <option value="sell_call">Sell Calls</option>
                    </>
                  )}
                </select>
              </div>

              {!stock && (
                <>
                  <div>
                    <label className="filter-heading cursor-help">
                      EXPIRATION
                      <span className="text-xs text-[color:var(--pf-muted)] ml-2">(requires ticker)</span>
                    </label>
                    <ExpirationDateSelect
                      ticker={row.ticker}
                      value={row.expiration}
                      onChange={(next) => handleRowChange(index, { expiration: next })}
                      disabled={false}
                    />
                  </div>

                  <div>
                    <label className="filter-heading cursor-help">
                      STRIKE
                      <span className="text-xs text-[color:var(--pf-muted)] ml-2">(requires ticker, type &amp; expiration)</span>
                    </label>
                    <StrikePriceSelect
                      ticker={row.ticker}
                      optionType={row.option_type}
                      expiration={row.expiration}
                      value={row.strike}
                      onChange={(next) => handleRowChange(index, { strike: next })}
                      disabled={false}
                    />
                  </div>

                  <div>
                    <label
                      className="filter-heading cursor-help"
                      data-tooltip-id="portfolio-tooltip"
                      data-tooltip-content="Your planned holding period in days. Defaults to half the time until expiration."
                    >
                      DAYS TO EXIT <span className="text-[color:var(--pf-muted)]">(optional)</span>
                    </label>
                    <input
                      type="number"
                      className="input"
                      value={row.days_to_gain || ''}
                      onChange={(e) => handleRowChange(index, { days_to_gain: e.target.value })}
                    />
                    <div className="h-5 mt-1" />
                  </div>
                </>
              )}

              <div>
                <label className="filter-heading">
                  {stock ? 'NUMBER OF SHARES' : 'NUMBER OF CONTRACTS'}{' '}
                  <span className="text-[color:var(--pf-muted)]">(optional)</span>
                </label>
                <input
                  type="number"
                  className="input"
                  value={row.number_of_contracts || ''}
                  onChange={(e) => handleRowChange(index, { number_of_contracts: e.target.value })}
                />
                <div className="h-5 mt-1" />
              </div>

              <div>
                <label
                  className="filter-heading cursor-help"
                  data-tooltip-id="portfolio-tooltip"
                  data-tooltip-content={
                    stock
                      ? 'What you actually paid per share. Used for cost basis and P&L. Defaults to the current price.'
                      : 'Your average purchase price per contract (per share). Used for P&L and equity tracking. Defaults to the current premium.'
                  }
                >
                  {stock ? 'AVG COST PER SHARE' : 'AVG COST PER CONTRACT'}{' '}
                  <span className="text-[color:var(--pf-muted)]">(optional)</span>
                </label>
                <input
                  type="number"
                  className="input"
                  value={row.average_cost_per_contract || ''}
                  onChange={(e) =>
                    handleRowChange(index, { average_cost_per_contract: e.target.value })
                  }
                />
              </div>
            </div>
          </div>
        );
      })}

      <div className="flex flex-col sm:flex-row gap-3">
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className={`${actionButtonTemplate.cssClasses.base} ${actionButtonTemplate.cssClasses.success} flex-1`}
          data-tooltip-id="portfolio-tooltip"
          data-tooltip-content="Add these positions to your portfolio and start tracking them."
        >
          {isSaving ? 'Adding...' : 'Add to Portfolio'}
        </button>
        <button
          type="button"
          onClick={() => handleAddRow(ASSET_TYPES.OPTION)}
          className={`${actionButtonTemplate.cssClasses.base} ${actionButtonTemplate.cssClasses.primary}`}
        >
          + Option
        </button>
        <button
          type="button"
          onClick={() => handleAddRow(ASSET_TYPES.STOCK)}
          className={`${actionButtonTemplate.cssClasses.base} ${actionButtonTemplate.cssClasses.primary}`}
        >
          + Stock
        </button>
      </div>

      {usageLimitData && (
        <UsageLimitModal data={usageLimitData} onClose={() => setUsageLimitData(null)} />
      )}
      {featureAccessData && (
        <FeatureAccessModal data={featureAccessData} onClose={() => setFeatureAccessData(null)} />
      )}

      {/* Own tooltip portal. The watchlist form borrows the one declared inside
          WatchlistContractCard, so its tooltips go dead when no card is mounted —
          don't inherit that. */}
      <ReactTooltip
        id="portfolio-tooltip"
        place="top"
        delayShow={300}
        delayHide={100}
        style={{
          backgroundColor: '#111827', /* --pf-shell */
          color: '#ffffff',
          borderRadius: '8px',
          padding: '8px 12px',
          fontSize: '13px',
          maxWidth: '280px',
          lineHeight: '1.4',
          zIndex: 1200,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
          border: '1px solid #374151', /* --pf-border */
        }}
      />
    </div>
  );
}
